/* ====================================================================
 * NPC STATS MODULE (R140-Z3p)
 * ====================================================================
 * Self-contained renderer + serializer for the optional D&D 5e NPC
 * stat block attached to each hero. Three public functions:
 *
 *   window.hohRenderNpcStatsForm(container, existingStats)
 *     Renders the form into `container`. Returns a `read()` function
 *     that serializes the current form state to a stats_block JSON.
 *
 *   window.hohRenderNpcStatsDisplay(container, statsObj)
 *     Renders the Monster Manual-style stat block + Copy-as-Markdown
 *     button into `container`.
 *
 *   window.hohNpcStatsAreUnlocked(charId)
 *     Single permission hook. Returns true today (free for everyone);
 *     will be swapped to a hearts-paid ledger check when the paywall
 *     phase ships. No other code needs to change when the gate turns on.
 *
 * Schema: { schema:"dnd5e-npc-v1", ... } — see plan doc for full shape.
 * Storage: characters.stats_block jsonb column.
 * ==================================================================== */
(function(){
  'use strict';

  /* The schema string is embedded so future schema migrations can
     detect old shapes and upgrade them client-side without a DB
     migration. */
  var STATS_SCHEMA_V1 = 'dnd5e-npc-v1';

  /* ---------- helpers ---------- */
  function esc(s){
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
      .replace(/"/g,'&quot;').replace(/'/g,'&#39;');
  }
  function modOf(score){
    var n = parseInt(score, 10);
    if(isNaN(n)) return 0;
    return Math.floor((n - 10) / 2);
  }
  function fmtMod(m){ return (m >= 0 ? '+' : '') + m; }
  function trim(s){ return (s == null ? '' : String(s)).trim(); }
  function intOrNull(s){
    var n = parseInt(s, 10);
    return isNaN(n) ? null : n;
  }
  function arrFromTextarea(s){
    /* Each line = one entry. Empty lines stripped. */
    return trim(s).split(/\n+/).map(trim).filter(Boolean);
  }

  /* Lightweight markdown — bold (**), italic (*), and line breaks.
     Used for trait/action body text. Avoids pulling in a full MD lib. */
  function tinyMd(s){
    if(!s) return '';
    var t = esc(s);
    t = t.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    t = t.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    return t.replace(/\n/g, '<br>');
  }

  /* ---------- permission hook ---------- */
  /* This is the SINGLE gate. When the paywall phase ships, the body
     of this function is replaced with a hearts-paid ledger check.
     Callers don't change. */
  window.hohNpcStatsAreUnlocked = function(charId){
    /* Phase 1: stats are free for everyone. Returning true unlocks
       the display path. The form gate (story-length) is separate. */
    return true;
  };

  /* ---------- defaults ---------- */
  function defaultStats(){
    return {
      schema: STATS_SCHEMA_V1,
      size: 'Medium',
      type: '',
      alignment: '',
      ac: { value: 12, source: '' },
      hp: { average: 20, formula: '' },
      speed: '30 ft.',
      abilities: { str:10, dex:10, con:10, int:10, wis:10, cha:10 },
      saves: {},
      skills: {},
      damage_resistances: [],
      damage_immunities: [],
      damage_vulnerabilities: [],
      senses: '',
      languages: '',
      challenge: { cr: '', xp: null },
      traits: [],
      actions: [],
      bonus_actions: [],
      reactions: []
    };
  }

  /* Normalize an incoming stats object (handles missing fields, old
     schemas). Returns a complete object suitable for rendering. */
  function normalize(stats){
    var d = defaultStats();
    if(!stats || typeof stats !== 'object') return d;
    var out = Object.assign(d, stats);
    out.ac = Object.assign(d.ac, stats.ac || {});
    out.hp = Object.assign(d.hp, stats.hp || {});
    out.abilities = Object.assign(d.abilities, stats.abilities || {});
    out.saves = Object.assign({}, stats.saves || {});
    out.skills = Object.assign({}, stats.skills || {});
    out.challenge = Object.assign(d.challenge, stats.challenge || {});
    out.traits = Array.isArray(stats.traits) ? stats.traits : [];
    out.actions = Array.isArray(stats.actions) ? stats.actions : [];
    out.bonus_actions = Array.isArray(stats.bonus_actions) ? stats.bonus_actions : [];
    out.reactions = Array.isArray(stats.reactions) ? stats.reactions : [];
    out.damage_resistances = Array.isArray(stats.damage_resistances) ? stats.damage_resistances : [];
    out.damage_immunities = Array.isArray(stats.damage_immunities) ? stats.damage_immunities : [];
    out.damage_vulnerabilities = Array.isArray(stats.damage_vulnerabilities) ? stats.damage_vulnerabilities : [];
    return out;
  }

  /* Detect whether a stats object has ANY meaningful content. Used so
     a blank form doesn't write an empty stats_block on submit. */
  function hasContent(stats){
    if(!stats) return false;
    if(trim(stats.type)) return true;
    if(trim(stats.alignment)) return true;
    if(stats.traits && stats.traits.length) return true;
    if(stats.actions && stats.actions.length) return true;
    /* Default values count as "user didn't fill it" — ignore them. */
    if((stats.ac && stats.ac.value !== 12) || (stats.hp && stats.hp.average !== 20)) return true;
    var ab = stats.abilities || {};
    var hasAbilities = ['str','dex','con','int','wis','cha'].some(function(k){ return ab[k] != null && ab[k] !== 10; });
    if(hasAbilities) return true;
    return false;
  }

  /* ====================================================================
     FORM RENDERER
     ==================================================================== */
  window.hohRenderNpcStatsForm = function(container, existingStats){
    if(!container) return function(){ return null; };
    var s = normalize(existingStats);

    /* Build the form HTML. Accordion sections so mobile users don't
       face a wall. Defaults pre-fill so a user can "fill the basics
       and skip the rest." */
    container.innerHTML =
      '<div class="hoh-npc-stats-form">' +
        '<div class="hoh-npc-stats-intro">' +
          '<strong>Make your hero playable.</strong> ' +
          'A filled stat block turns your character into a ready-to-run NPC anyone can drop into a session. ' +
          'It also adds real depth to your card so it stands out across the Hall. ' +
          'Skip any field you don\'t want to fill.' +
        '</div>' +
        /* R433 (Jan): "dont add the * on those since the whole thing is
           optional anyways." The asterisks and this legend were mirroring
           _hohStatsComplete's bonus contract, but a star on an optional
           field means one thing to everybody who has ever filled a form:
           you cannot submit without this. It was scaring people off a
           section they could simply walk past. The bonus still exists and
           the trigger row still advertises it; the fields stop lying. */
        '<p class="hoh-req-legend">Fill the type, add at least one action, and move one number off its default to earn the ticket above. Skip the rest.</p>' +

        /* SECTION 1: Basics */
        '<details class="hoh-npc-stats-section" open>' +
          '<summary class="hoh-npc-stats-summary">Basics</summary>' +
          '<div class="hoh-npc-stats-grid">' +
            '<label class="hoh-npc-stats-field">' +
              '<span>Size</span>' +
              '<select id="hoh-stats-size" class="hoh-npc-stats-input">' +
                ['Tiny','Small','Medium','Large','Huge','Gargantuan'].map(function(sz){
                  return '<option value="' + sz + '"' + (sz === s.size ? ' selected' : '') + '>' + sz + '</option>';
                }).join('') +
              '</select>' +
            '</label>' +
            '<label class="hoh-npc-stats-field">' +
              '<span>Type</span>' +
              '<input id="hoh-stats-type" class="hoh-npc-stats-input" type="text" placeholder="humanoid (elf)" value="' + esc(s.type) + '">' +
            '</label>' +
            '<label class="hoh-npc-stats-field hoh-npc-stats-field-wide">' +
              '<span>Alignment</span>' +
              '<input id="hoh-stats-alignment" class="hoh-npc-stats-input" type="text" placeholder="Chaotic Good" value="' + esc(s.alignment) + '">' +
            '</label>' +
          '</div>' +
        '</details>' +

        /* SECTION 2: AC / HP / Speed */
        '<details class="hoh-npc-stats-section" open>' +
          '<summary class="hoh-npc-stats-summary">Armor, HP, Speed</summary>' +
          '<div class="hoh-npc-stats-grid">' +
            '<label class="hoh-npc-stats-field">' +
              '<span>Armor Class</span>' +
              '<input id="hoh-stats-ac-value" class="hoh-npc-stats-input" type="number" min="1" max="30" value="' + esc(s.ac.value) + '">' +
            '</label>' +
            '<label class="hoh-npc-stats-field">' +
              '<span>AC Source</span>' +
              '<input id="hoh-stats-ac-source" class="hoh-npc-stats-input" type="text" placeholder="leather armor" value="' + esc(s.ac.source) + '">' +
            '</label>' +
            '<label class="hoh-npc-stats-field">' +
              '<span>HP Average</span>' +
              '<input id="hoh-stats-hp-avg" class="hoh-npc-stats-input" type="number" min="1" value="' + esc(s.hp.average) + '">' +
            '</label>' +
            '<label class="hoh-npc-stats-field">' +
              '<span>HP Formula</span>' +
              '<input id="hoh-stats-hp-formula" class="hoh-npc-stats-input" type="text" placeholder="6d8+12" value="' + esc(s.hp.formula) + '">' +
            '</label>' +
            '<label class="hoh-npc-stats-field hoh-npc-stats-field-wide">' +
              '<span>Speed</span>' +
              '<input id="hoh-stats-speed" class="hoh-npc-stats-input" type="text" placeholder="30 ft." value="' + esc(s.speed) + '">' +
            '</label>' +
          '</div>' +
        '</details>' +

        /* SECTION 3: Ability scores (6-stat grid, 3 cols × 2 rows on mobile) */
        '<details class="hoh-npc-stats-section" open>' +
          '<summary class="hoh-npc-stats-summary">Ability Scores</summary>' +
          '<div class="hoh-npc-stats-abilities">' +
            ['str','dex','con','int','wis','cha'].map(function(ab){
              var v = s.abilities[ab];
              return '<label class="hoh-npc-stats-ability">' +
                '<span class="hoh-npc-stats-ability-label">' + ab.toUpperCase() + '</span>' +
                '<input id="hoh-stats-ab-' + ab + '" class="hoh-npc-stats-ability-input" type="number" min="1" max="30" value="' + esc(v) + '" data-ability="' + ab + '">' +
                '<span class="hoh-npc-stats-ability-mod" id="hoh-stats-ab-' + ab + '-mod">' + fmtMod(modOf(v)) + '</span>' +
              '</label>';
            }).join('') +
          '</div>' +
        '</details>' +

        /* SECTION 4: Saves / Skills / Senses / Languages */
        '<details class="hoh-npc-stats-section" open>' +
          '<summary class="hoh-npc-stats-summary">Saves, Skills, Senses</summary>' +
          '<div class="hoh-npc-stats-grid">' +
            '<label class="hoh-npc-stats-field hoh-npc-stats-field-wide">' +
              '<span>Saving Throws</span>' +
              '<input id="hoh-stats-saves" class="hoh-npc-stats-input" type="text" placeholder="Dex +5, Wis +4" value="' + esc(formatModMap(s.saves)) + '">' +
            '</label>' +
            '<label class="hoh-npc-stats-field hoh-npc-stats-field-wide">' +
              '<span>Skills</span>' +
              '<input id="hoh-stats-skills" class="hoh-npc-stats-input" type="text" placeholder="Perception +5, Stealth +7" value="' + esc(formatModMap(s.skills)) + '">' +
            '</label>' +
            '<label class="hoh-npc-stats-field hoh-npc-stats-field-wide">' +
              '<span>Senses</span>' +
              '<input id="hoh-stats-senses" class="hoh-npc-stats-input" type="text" placeholder="darkvision 60 ft., passive Perception 15" value="' + esc(s.senses) + '">' +
            '</label>' +
            '<label class="hoh-npc-stats-field hoh-npc-stats-field-wide">' +
              '<span>Languages</span>' +
              '<input id="hoh-stats-languages" class="hoh-npc-stats-input" type="text" placeholder="Common, Elvish" value="' + esc(s.languages) + '">' +
            '</label>' +
            '<label class="hoh-npc-stats-field">' +
              '<span>Challenge Rating</span>' +
              '<input id="hoh-stats-cr" class="hoh-npc-stats-input" type="text" placeholder="3" value="' + esc(s.challenge.cr) + '">' +
            '</label>' +
            '<label class="hoh-npc-stats-field">' +
              '<span>XP</span>' +
              '<input id="hoh-stats-xp" class="hoh-npc-stats-input" type="number" min="0" placeholder="700" value="' + esc(s.challenge.xp == null ? '' : s.challenge.xp) + '">' +
            '</label>' +
          '</div>' +
        '</details>' +

        /* SECTION 5: Traits + Actions (textareas, one entry per line:
           "Name | Description") */
        '<details class="hoh-npc-stats-section" open>' +
          '<summary class="hoh-npc-stats-summary">Traits &amp; Actions</summary>' +
          '<div class="hoh-npc-stats-grid">' +
            '<label class="hoh-npc-stats-field hoh-npc-stats-field-wide">' +
              '<span>Traits</span>' +
              '<textarea id="hoh-stats-traits" class="hoh-npc-stats-textarea" rows="3" placeholder="One per line. Format: Name | Description\nFey Ancestry | Advantage on saves against being charmed.">' + esc(joinTraits(s.traits)) + '</textarea>' +
            '</label>' +
            '<label class="hoh-npc-stats-field hoh-npc-stats-field-wide">' +
              '<span>Actions</span>' +
              '<textarea id="hoh-stats-actions" class="hoh-npc-stats-textarea" rows="4" placeholder="One per line. Format: Name | Description\nLongsword | Melee Weapon Attack: +5 to hit, 1d8+3 slashing damage.">' + esc(joinTraits(s.actions)) + '</textarea>' +
            '</label>' +
            '<label class="hoh-npc-stats-field hoh-npc-stats-field-wide">' +
              '<span>Bonus Actions</span>' +
              '<textarea id="hoh-stats-bonus-actions" class="hoh-npc-stats-textarea" rows="2" placeholder="Cunning Action | Bonus action to Dash, Disengage, or Hide.">' + esc(joinTraits(s.bonus_actions)) + '</textarea>' +
            '</label>' +
            '<label class="hoh-npc-stats-field hoh-npc-stats-field-wide">' +
              '<span>Reactions</span>' +
              '<textarea id="hoh-stats-reactions" class="hoh-npc-stats-textarea" rows="2" placeholder="Parry | +2 AC against one melee attack you can see.">' + esc(joinTraits(s.reactions)) + '</textarea>' +
            '</label>' +
          '</div>' +
        '</details>' +
      '</div>';

    /* Wire the ability-score live modifier readout. */
    ['str','dex','con','int','wis','cha'].forEach(function(ab){
      var inp = document.getElementById('hoh-stats-ab-' + ab);
      var modEl = document.getElementById('hoh-stats-ab-' + ab + '-mod');
      if(inp && modEl){
        inp.addEventListener('input', function(){
          modEl.textContent = fmtMod(modOf(inp.value));
        });
      }
    });

    /* Return the serializer. The submission flow calls this on submit
       to read the current form into a stats_block JSON. */
    return function readStats(){
      var raw = {
        schema: STATS_SCHEMA_V1,
        size: val('hoh-stats-size'),
        type: trim(val('hoh-stats-type')),
        alignment: trim(val('hoh-stats-alignment')),
        ac: { value: intOrNull(val('hoh-stats-ac-value')), source: trim(val('hoh-stats-ac-source')) },
        hp: { average: intOrNull(val('hoh-stats-hp-avg')), formula: trim(val('hoh-stats-hp-formula')) },
        speed: trim(val('hoh-stats-speed')),
        abilities: {
          str: intOrNull(val('hoh-stats-ab-str')),
          dex: intOrNull(val('hoh-stats-ab-dex')),
          con: intOrNull(val('hoh-stats-ab-con')),
          int: intOrNull(val('hoh-stats-ab-int')),
          wis: intOrNull(val('hoh-stats-ab-wis')),
          cha: intOrNull(val('hoh-stats-ab-cha'))
        },
        saves: parseModMap(val('hoh-stats-saves')),
        skills: parseModMap(val('hoh-stats-skills')),
        senses: trim(val('hoh-stats-senses')),
        languages: trim(val('hoh-stats-languages')),
        challenge: { cr: trim(val('hoh-stats-cr')), xp: intOrNull(val('hoh-stats-xp')) },
        traits: parseTraits(val('hoh-stats-traits')),
        actions: parseTraits(val('hoh-stats-actions')),
        bonus_actions: parseTraits(val('hoh-stats-bonus-actions')),
        reactions: parseTraits(val('hoh-stats-reactions'))
      };
      /* Return null when the form has no meaningful content so the
         submit flow doesn't write a fully-default block to the DB. */
      return hasContent(raw) ? raw : null;
    };

    function val(id){ var el = document.getElementById(id); return el ? el.value : ''; }
  };

  /* Save/restore helpers for trait-style key|desc lists. */
  function joinTraits(arr){
    if(!Array.isArray(arr)) return '';
    return arr.map(function(t){
      if(!t) return '';
      return (t.name || '') + ' | ' + (t.desc || '');
    }).filter(function(line){ return line && line !== ' | '; }).join('\n');
  }
  function parseTraits(s){
    return arrFromTextarea(s).map(function(line){
      var idx = line.indexOf('|');
      if(idx === -1) return { name: line, desc: '' };
      return { name: trim(line.slice(0, idx)), desc: trim(line.slice(idx + 1)) };
    }).filter(function(t){ return t.name; });
  }

  /* "Dex +5, Wis +4" ↔ { dex:5, wis:4 } */
  function formatModMap(m){
    if(!m) return '';
    return Object.keys(m).map(function(k){
      var v = m[k];
      return cap1(k) + ' ' + fmtMod(parseInt(v, 10) || 0);
    }).join(', ');
  }
  function cap1(s){ return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; }
  function parseModMap(s){
    var out = {};
    trim(s).split(/\s*,\s*/).filter(Boolean).forEach(function(seg){
      /* Match "Word +N" or "Word -N" */
      var m = seg.match(/^([A-Za-z][A-Za-z ]*)\s*([+\-]\s*\d+)$/);
      if(m){
        var key = trim(m[1]).toLowerCase();
        var n = parseInt(m[2].replace(/\s+/g, ''), 10);
        if(!isNaN(n)) out[key] = n;
      }
    });
    return out;
  }

  /* ====================================================================
     DISPLAY RENDERER
     ==================================================================== */
  window.hohRenderNpcStatsDisplay = function(container, statsObj){
    if(!container) return;
    if(!statsObj){
      container.innerHTML = '';
      return;
    }
    var s = normalize(statsObj);
    var mods = {
      str: modOf(s.abilities.str), dex: modOf(s.abilities.dex),
      con: modOf(s.abilities.con), int: modOf(s.abilities.int),
      wis: modOf(s.abilities.wis), cha: modOf(s.abilities.cha)
    };

    var html =
      '<div class="hoh-npc-stats-display">' +
        '<div class="hoh-npc-stats-display-header">' +
          '<span class="hoh-npc-stats-display-title">NPC Stat Block</span>' +
          '<button type="button" class="hoh-npc-stats-copy" onclick="hohNpcStatsCopyMd(this)">Copy as Markdown</button>' +
        '</div>' +
        '<div class="hoh-npc-stats-display-body">' +
          '<div class="hoh-npc-stats-display-meta">' +
            esc([s.size, s.type, s.alignment].filter(Boolean).join(' ')) +
          '</div>' +
          '<div class="hoh-npc-stats-display-rule"></div>' +
          (s.ac.value ? '<div><strong>Armor Class</strong> ' + esc(s.ac.value) + (s.ac.source ? ' (' + esc(s.ac.source) + ')' : '') + '</div>' : '') +
          (s.hp.average ? '<div><strong>Hit Points</strong> ' + esc(s.hp.average) + (s.hp.formula ? ' (' + esc(s.hp.formula) + ')' : '') + '</div>' : '') +
          (s.speed ? '<div><strong>Speed</strong> ' + esc(s.speed) + '</div>' : '') +
          '<div class="hoh-npc-stats-display-rule"></div>' +
          '<div class="hoh-npc-stats-display-abilities">' +
            ['str','dex','con','int','wis','cha'].map(function(ab){
              return '<div class="hoh-npc-stats-display-ability">' +
                '<span class="hoh-npc-stats-display-ability-label">' + ab.toUpperCase() + '</span>' +
                '<span class="hoh-npc-stats-display-ability-value">' + esc(s.abilities[ab]) + ' (' + fmtMod(mods[ab]) + ')</span>' +
              '</div>';
            }).join('') +
          '</div>' +
          '<div class="hoh-npc-stats-display-rule"></div>' +
          (Object.keys(s.saves).length ? '<div><strong>Saving Throws</strong> ' + esc(formatModMap(s.saves)) + '</div>' : '') +
          (Object.keys(s.skills).length ? '<div><strong>Skills</strong> ' + esc(formatModMap(s.skills)) + '</div>' : '') +
          (s.senses ? '<div><strong>Senses</strong> ' + esc(s.senses) + '</div>' : '') +
          (s.languages ? '<div><strong>Languages</strong> ' + esc(s.languages) + '</div>' : '') +
          (s.challenge.cr ? '<div><strong>Challenge</strong> ' + esc(s.challenge.cr) + (s.challenge.xp ? ' (' + esc(s.challenge.xp) + ' XP)' : '') + '</div>' : '') +
          (s.traits.length ? renderEntries('Traits', s.traits) : '') +
          (s.actions.length ? renderEntries('Actions', s.actions) : '') +
          (s.bonus_actions.length ? renderEntries('Bonus Actions', s.bonus_actions) : '') +
          (s.reactions.length ? renderEntries('Reactions', s.reactions) : '') +
        '</div>' +
        /* Hidden JSON for copy-to-markdown to read. Avoids re-serializing. */
        '<script type="application/json" class="hoh-npc-stats-source">' + JSON.stringify(s) + '<\\/script>' +
      '</div>';

    container.innerHTML = html;
  };
  function renderEntries(label, entries){
    var inner = entries.map(function(e){
      if(!e || !e.name) return '';
      return '<div class="hoh-npc-stats-entry"><strong>' + esc(e.name) + '.</strong> ' + tinyMd(e.desc || '') + '</div>';
    }).join('');
    return '<div class="hoh-npc-stats-display-rule"></div>' +
      '<div class="hoh-npc-stats-section-label">' + esc(label) + '</div>' +
      inner;
  }

  /* ====================================================================
     COPY AS MARKDOWN
     ==================================================================== */
  window.hohNpcStatsCopyMd = function(btn){
    try {
      var wrap = btn.closest('.hoh-npc-stats-display');
      if(!wrap) return;
      var src = wrap.querySelector('.hoh-npc-stats-source');
      if(!src) return;
      var s = normalize(JSON.parse(src.textContent || '{}'));
      var lines = [];
      lines.push('***');
      lines.push('*' + [s.size, s.type, s.alignment].filter(Boolean).join(' ') + '*');
      lines.push('');
      if(s.ac.value) lines.push('**Armor Class** ' + s.ac.value + (s.ac.source ? ' (' + s.ac.source + ')' : ''));
      if(s.hp.average) lines.push('**Hit Points** ' + s.hp.average + (s.hp.formula ? ' (' + s.hp.formula + ')' : ''));
      if(s.speed) lines.push('**Speed** ' + s.speed);
      lines.push('');
      var ab = s.abilities;
      lines.push('| STR | DEX | CON | INT | WIS | CHA |');
      lines.push('|-----|-----|-----|-----|-----|-----|');
      lines.push('| ' + [ab.str,ab.dex,ab.con,ab.int,ab.wis,ab.cha].map(function(v){
        return v + ' (' + fmtMod(modOf(v)) + ')';
      }).join(' | ') + ' |');
      lines.push('');
      if(Object.keys(s.saves).length) lines.push('**Saving Throws** ' + formatModMap(s.saves));
      if(Object.keys(s.skills).length) lines.push('**Skills** ' + formatModMap(s.skills));
      if(s.senses) lines.push('**Senses** ' + s.senses);
      if(s.languages) lines.push('**Languages** ' + s.languages);
      if(s.challenge.cr) lines.push('**Challenge** ' + s.challenge.cr + (s.challenge.xp ? ' (' + s.challenge.xp + ' XP)' : ''));
      ['traits','actions','bonus_actions','reactions'].forEach(function(k){
        var arr = s[k]; if(!arr || !arr.length) return;
        var label = k === 'bonus_actions' ? 'Bonus Actions' : (k.charAt(0).toUpperCase() + k.slice(1));
        lines.push('');
        lines.push('### ' + label);
        arr.forEach(function(e){
          if(!e || !e.name) return;
          lines.push('***' + e.name + '.*** ' + (e.desc || ''));
        });
      });
      lines.push('***');
      var md = lines.join('\n');
      try {
        if(navigator.clipboard && navigator.clipboard.writeText){
          navigator.clipboard.writeText(md);
        } else {
          var ta = document.createElement('textarea');
          ta.value = md;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        btn.textContent = 'Copied!';
        setTimeout(function(){ btn.textContent = 'Copy as Markdown'; }, 1600);
      } catch(_){}
    } catch(_){}
  };

  /* Expose the schema helpers in case the data layer wants them. */
  window.HOH_NPC_STATS = {
    SCHEMA: STATS_SCHEMA_V1,
    defaultStats: defaultStats,
    normalize: normalize,
    hasContent: hasContent
  };

})();
